import type { PublishThemeId } from './types';

/**
 * Theme palettes for published mini-apps.
 *
 * These are *runtime* CSS-variable bundles — the composer paints them
 * onto the preview, and the bundle generator inlines them into the
 * published HTML. Different from the studio's own theme tokens
 * because the published page is a different visual context (often
 * full-bleed, kid-shareable) and we want a curated set rather than
 * inheriting the studio chrome.
 */

export interface PublishTheme {
  id: PublishThemeId;
  /** Kid-facing name. */
  label: string;
  /** Short tagline shown under the swatch. */
  hint: string;
  /** Background canvas for the published page. */
  bg: string;
  /** Foreground / text. */
  fg: string;
  /** Accent — used for buttons + bursts. */
  accent: string;
  /** Text colour to pair with `accent` as a background (e.g. CTA
   *  button). Curated per-theme so we never end up with light text on
   *  a light yellow accent (Lab / Paper). Always passes 4.5:1
   *  against `accent`. */
  accentFg: string;
  /** Secondary accent — used for highlights, focus outlines. */
  secondary: string;
  /** Surface inside the page (cards / modal panels). */
  surface: string;
  /** Optional gradient layered over the bg. CSS background value. */
  gradient?: string;
}

export const PUBLISH_THEMES: ReadonlyArray<PublishTheme> = [
  {
    id: 'lab',
    label: 'Lab notebook',
    hint: 'Graph-paper green, ink blue, citrus + magenta',
    bg: '#eef4eb',
    fg: '#142a4c',
    accent: '#ffd23f',
    // Dark navy on yellow → 9.6:1.
    accentFg: '#142a4c',
    secondary: '#be1a73',
    surface: '#ffffff',
  },
  {
    id: 'paper',
    label: 'Studio paper',
    hint: 'Cream, deep ink, lemon + rust',
    bg: '#f7f1e1',
    fg: '#1f1b14',
    accent: '#f4c430',
    // Deep ink on lemon → 11.3:1.
    accentFg: '#1f1b14',
    secondary: '#a83227',
    surface: '#fffaf0',
  },
  {
    id: 'riso',
    label: 'Risograph',
    hint: 'Eggplant, mint, apricot, off-white',
    bg: '#1d1426',
    fg: '#fbf6e9',
    accent: '#6fe3b3',
    // Eggplant on mint → 11.3:1.
    accentFg: '#1d1426',
    secondary: '#ffb37c',
    surface: '#2a1f37',
  },
  {
    id: 'sunset',
    label: 'Sunset',
    hint: 'Warm pink to indigo, gold accents',
    bg: '#1f0f1d',
    fg: '#fef5ec',
    accent: '#ffb86b',
    // Indigo-purple on peach → 8.6:1.
    accentFg: '#1f0f1d',
    secondary: '#ff5d8f',
    surface: '#2d1830',
    gradient: 'linear-gradient(160deg, #401736 0%, #1f0f1d 60%, #0a0c1f 100%)',
  },
  {
    id: 'ocean',
    label: 'Ocean',
    hint: 'Deep teal, cyan, soft white',
    bg: '#062c33',
    fg: '#eaf7f5',
    accent: '#7be3d3',
    // Deep teal on cyan → 9.7:1.
    accentFg: '#062c33',
    secondary: '#ffd166',
    surface: '#0c3d47',
    gradient: 'radial-gradient(ellipse at 30% 0%, #0e5764 0%, #062c33 60%, #02161b 100%)',
  },
];

export function getPublishTheme(id: PublishThemeId): PublishTheme {
  return PUBLISH_THEMES.find((t) => t.id === id) ?? PUBLISH_THEMES[0]!;
}
